/**
 * Emoji-keszlet a reakciokhoz es a szerkesztohoz.
 *
 * A kulcsszavak MAGYARUL vannak, mert magyarul keresunk. Ez a keszlet lenyege:
 * egy angol nevu teljes Unicode-lista technikailag "tobbet tud", de aki
 * "kutya"-t ir be, annak nem segit, hogy a jel neve `dog face`.
 *
 * Az elso kulcsszo egyben a jel NEVE - ez jelenik meg a buboreksugoban.
 *
 * ─── Miert nem a teljes Unicode-keszlet? ────────────────────────────────────
 *
 * Mert a teljes keszlet ~3800 jel, aminek a nagy resze zaszlo, bortonus es
 * csaladi kombinacio - napi hasznalatban soha senki nem keresi oket, viszont
 * lassuva teszik a gorgetest es feltoltik a talalati listat. Ami itt van, az a
 * gyakorlatban hasznalt keszlet, magyar nevekkel: irodai kommunikacio,
 * hangulat, allatorvosi kornyezet, etel, utazas, targyak, szimbolumok.
 *
 * Ha valakinek hianyzik valami, vedd fel a megfelelo csoportba - a fajl
 * szandekosan sima adat, nincs benne logika.
 */
import { EMOJI_ALIASES } from './emoji-alias';

export interface EmojiEntry {
  char: string;
  /** Kereso-kulcsszavak. Az elso egyben a jel neve is. */
  keywords: string[];
}

export interface EmojiGroup {
  title: string;
  items: EmojiEntry[];
}

/** Rovid alak: a `|` jellel elvalasztott kulcsszavak listava alakulnak. */
const e = (char: string, keywords: string): EmojiEntry => ({
  char,
  keywords: keywords.split('|'),
});

export const EMOJI_GROUPS: EmojiGroup[] = [
  {
    /*
      A munkahelyi reakciok alapkeszlete.

      Regen ez volt a "Gyakori" sor, mindenkinek ugyanaz. A "Gyakori" azota a
      SAJAT hasznalatbol all ossze (lasd data/emoji-usage.ts); ez a csoport
      bongeszheto kategoriakent marad, es akinek meg nincs elozmenye, annak ez
      all legelol.
    */
    title: 'Reakciók',
    items: [
      e('👍', 'felfelé hüvelyk|ok|jó|rendben|igen'),
      e('👎', 'lefelé hüvelyk|nem|rossz'),
      e('✅', 'pipa|kész|ok|megvan|elintézve'),
      e('❌', 'x|nem|hiba|törölve'),
      e('❤️', 'szív|szeretet|imádom'),
      e('🎉', 'konfetti|ünneplés|gratula|siker'),
      e('👀', 'szemek|nézem|figyelem|megnézem'),
      e('🙏', 'köszönöm|kérlek|hálás'),
      e('🔥', 'tűz|nagyon jó|menő'),
      e('💡', 'ötlet|izzó|javaslat'),
      e('🤔', 'gondolkodik|hmm|kérdés'),
      e('⚠️', 'figyelmeztetés|vigyázat|veszély'),
      e('🚀', 'rakéta|indítás|gyors|kiadás'),
      e('⏳', 'homokóra|várakozás|folyamatban'),
      e('📌', 'rajzszög|kitűzve|fontos'),
      e('🆗', 'ok|rendben'),
      e('😂', 'könnyes nevetés|röhej|vicces'),
      e('🙌', 'felemelt kezek|hurrá|siker'),
      e('💪', 'izom|erő|hajrá'),
      e('👏', 'taps|bravó|gratulálok'),
      e('🤝', 'kézfogás|megegyeztünk|üzlet'),
      e('🫡', 'tisztelgés|értettem'),
    ],
  },
  {
    title: 'Arcok és hangulat',
    items: [
      e('😀', 'mosoly|vidám|öröm'),
      e('😃', 'széles mosoly|vidám'),
      e('😄', 'nevet|boldog'),
      e('😁', 'vigyor|elégedett'),
      e('😆', 'hangos nevetés|hahota'),
      e('😅', 'ideges nevetés|huhh|megúsztuk'),
      e('🤣', 'földön fetreng|röhög'),
      e('😊', 'piruló mosoly|kedves'),
      e('🙂', 'halvány mosoly|ok'),
      e('🙃', 'fejjel lefelé|irónia'),
      e('😉', 'kacsintás'),
      e('😌', 'megkönnyebbült|nyugodt'),
      e('😍', 'szív szemek|imádom'),
      e('🥰', 'szívecskés|szeretet'),
      e('😘', 'csók'),
      e('😗', 'csókol'),
      e('😙', 'mosolygó csók'),
      e('😚', 'csukott szemű csók'),
      e('😋', 'nyalja a száját|finom'),
      e('😛', 'nyelvet nyújt'),
      e('😜', 'kacsintós nyelv|huncut'),
      e('🤪', 'bolondos|hülyéskedik'),
      e('😝', 'csukott szemű nyelv'),
      e('🤑', 'pénz szemek|nyereség'),
      e('🤗', 'ölelés|megölel'),
      e('🤭', 'kuncog|hoppá'),
      e('🤫', 'pszt|csend|titok'),
      e('🤐', 'cipzáras száj|hallgat'),
      e('🤨', 'felvont szemöldök|kétkedés'),
      e('😐', 'semleges|közömbös'),
      e('😑', 'kifejezéstelen|unott'),
      e('😶', 'néma|szótlan'),
      e('😏', 'somolyog|sunyi'),
      e('😒', 'unott|elégedetlen'),
      e('🙄', 'égre néz|na ne'),
      e('😬', 'fintor|kínos'),
      e('🤥', 'hazudik|pinokkió'),
      e('😔', 'lehangolt|szomorú'),
      e('😪', 'álmos'),
      e('🤤', 'nyáladzik'),
      e('😴', 'alszik|zzz'),
      e('😷', 'maszk|beteg'),
      e('🤒', 'lázas|beteg'),
      e('🤕', 'sérült|kötés'),
      e('🤢', 'hányinger|undor'),
      e('🤮', 'hány|rosszul van'),
      e('🤧', 'tüsszent|náthás'),
      e('🥵', 'melege van|hőség'),
      e('🥶', 'fázik|hideg'),
      e('🥴', 'kótyagos|szédül'),
      e('😵', 'kiütve|szédül'),
      e('🤯', 'felrobban a fejem|döbbenet'),
      e('🤠', 'cowboy'),
      e('🥳', 'bulizik|ünneplés'),
      e('😎', 'napszemüveg|menő'),
      e('🤓', 'stréber|okoska'),
      e('🧐', 'monoklis|vizsgálódik'),
      e('😕', 'zavart|értetlen'),
      e('😟', 'aggódik'),
      e('🙁', 'szomorkás'),
      e('☹️', 'szomorú'),
      e('😮', 'meglepett|ó'),
      e('😯', 'elképedt'),
      e('😲', 'döbbent|hűha'),
      e('😳', 'elpirul|kínos'),
      e('🥺', 'könyörgő|kérlek'),
      e('😦', 'riadt'),
      e('😧', 'rémült'),
      e('😨', 'ijedt'),
      e('😰', 'hideg veríték|szorong'),
      e('😥', 'csalódott|megkönnyebbült'),
      e('😢', 'sír|szomorú'),
      e('😭', 'zokog|nagyon szomorú'),
      e('😱', 'sikoly|rémület'),
      e('😖', 'gyötrődik'),
      e('😣', 'kitart|erőlködik'),
      e('😞', 'csalódott'),
      e('😓', 'verejtékezik|fáradt'),
      e('😩', 'kimerült|elege van'),
      e('😫', 'fáradt|nyűgös'),
      e('🥱', 'ásít|unatkozik'),
      e('😤', 'füstölög|dühös'),
      e('😡', 'dühös|mérges'),
      e('😠', 'haragos'),
      e('🤬', 'káromkodik'),
      e('😇', 'angyali|ártatlan'),
      e('🥲', 'könnyes mosoly'),
      e('🫠', 'elolvad|kínos'),
      e('🫥', 'pontozott arc|láthatatlan'),
      e('😈', 'kaján ördög'),
      e('👿', 'dühös ördög'),
      e('💀', 'koponya|halott'),
      e('☠️', 'koponya csontokkal|veszély'),
      e('💩', 'kaki'),
      e('🤡', 'bohóc'),
      e('👹', 'szörny'),
      e('👻', 'szellem'),
      e('👽', 'földönkívüli'),
      e('🤖', 'robot|bot|gép'),
      e('😺', 'vidám macska'),
      e('😸', 'vigyorgó macska'),
      e('😹', 'nevető macska'),
      e('😻', 'szerelmes macska'),
      e('😼', 'sunyi macska'),
      e('🙀', 'rémült macska'),
      e('😿', 'síró macska'),
      e('😾', 'mérges macska'),
      e('🎃', 'töklámpás|halloween'),
    ],
  },
  {
    title: 'Kéz és ember',
    items: [
      e('👋', 'integetés|szia|helló'),
      e('🤚', 'felemelt kézhát'),
      e('🖐️', 'széttárt ujjak'),
      e('✋', 'megállj|stop|tenyér'),
      e('🖖', 'vulkáni köszöntés'),
      e('👌', 'oké|tökéletes'),
      e('🤌', 'csippentett ujjak|na de'),
      e('🤏', 'kis mennyiség|egy kicsit'),
      e('✌️', 'béke|győzelem'),
      e('🤞', 'keresztbe tett ujjak|szorítok'),
      e('🫰', 'ujjszív|szeretet'),
      e('🤟', 'szeretlek'),
      e('🤘', 'rock'),
      e('🤙', 'hívj fel|laza'),
      e('👈', 'balra mutat'),
      e('👉', 'jobbra mutat'),
      e('👆', 'felfelé mutat|fentebb'),
      e('👇', 'lefelé mutat|lentebb'),
      e('☝️', 'felfelé mutató ujj'),
      e('✊', 'ökölbe szorít|szolidaritás'),
      e('👊', 'ökölpacsi'),
      e('🤛', 'bal ököl'),
      e('🤜', 'jobb ököl'),
      e('🫶', 'szív kézzel|szeretet'),
      e('✍️', 'ír|aláírás'),
      e('💅', 'körömlakk|nem érdekel'),
      e('🤳', 'szelfi'),
      e('💄', 'rúzs'),
      e('🦾', 'protézis kar|erő'),
      e('🦿', 'protézis láb'),
      e('🦵', 'láb'),
      e('🦶', 'lábfej'),
      e('👂', 'fül|hallgatom'),
      e('🦻', 'hallókészülék'),
      e('👃', 'orr|szagol'),
      e('🧠', 'agy|ész|gondolat'),
      e('🫀', 'szív szerv'),
      e('🫁', 'tüdő'),
      e('🦷', 'fog'),
      e('🦴', 'csont'),
      e('👁️', 'szem|látom'),
      e('👅', 'nyelv'),
      e('👄', 'száj'),
      e('👶', 'baba|csecsemő'),
      e('🧒', 'gyerek'),
      e('👦', 'fiú'),
      e('👧', 'lány'),
      e('🧑', 'személy'),
      e('👱', 'szőke'),
      e('👨', 'férfi'),
      e('🧔', 'szakállas'),
      e('👩', 'nő'),
      e('🧓', 'idős ember'),
      e('👴', 'nagyapa'),
      e('👵', 'nagymama'),
      e('🙍', 'homlokát ráncolja'),
      e('🙎', 'duzzog'),
      e('🙅', 'nem|tiltás'),
      e('🙆', 'rendben|jóváhagy'),
      e('💁', 'információ|tessék'),
      e('🙋', 'jelentkezik|én'),
      e('🧏', 'siket'),
      e('🙇', 'meghajol|bocsánat'),
      e('🤦', 'arctenyér|jaj ne'),
      e('🤷', 'vállrándítás|nem tudom'),
      e('👮', 'rendőr'),
      e('🕵️', 'nyomozó|keres'),
      e('💂', 'őr'),
      e('👷', 'építőmunkás|munka'),
      e('🤴', 'herceg'),
      e('👸', 'hercegnő'),
      e('👰', 'menyasszony'),
      e('🤵', 'vőlegény'),
      e('🎅', 'mikulás'),
      e('🦸', 'szuperhős|hős'),
      e('🦹', 'gonosztevő'),
      e('🧙', 'varázsló'),
      e('🧚', 'tündér'),
      e('🧛', 'vámpír'),
      e('👨‍⚕️', 'orvos|doktor'),
      e('👩‍⚕️', 'orvosnő|doktornő'),
      e('👨‍🌾', 'gazda'),
      e('👩‍🌾', 'gazdaasszony'),
      e('👨‍🍳', 'szakács'),
      e('👩‍🍳', 'szakácsnő'),
      e('👨‍💻', 'programozó|fejlesztő'),
      e('👩‍💻', 'programozónő|fejlesztő'),
      e('👨‍🔧', 'szerelő'),
      e('👩‍🔬', 'kutató|laboráns'),
      e('👨‍🏫', 'tanár'),
      e('👩‍🏫', 'tanárnő'),
      e('🚶', 'sétál|gyalog'),
      e('🏃', 'fut|siet'),
      e('💃', 'táncoló nő'),
      e('🕺', 'táncoló férfi'),
      e('🧘', 'jóga|meditáció|nyugalom'),
      e('🛌', 'ágyban|alvás'),
      e('👪', 'család'),
      e('🫂', 'ölelés|összetartás'),
    ],
  },
  {
    title: 'Állatok',
    items: [
      e('🐶', 'kutya|kutyus|eb'),
      e('🐕', 'kutya'),
      e('🦮', 'vakvezető kutya'),
      e('🐕‍🦺', 'segítő kutya'),
      e('🐩', 'uszkár'),
      e('🐺', 'farkas'),
      e('🦊', 'róka'),
      e('🦝', 'mosómedve'),
      e('🐱', 'macska|cica'),
      e('🐈', 'macska'),
      e('🐈‍⬛', 'fekete macska'),
      e('🦁', 'oroszlán'),
      e('🐯', 'tigris'),
      e('🐅', 'tigris'),
      e('🐆', 'leopárd'),
      e('🐴', 'ló'),
      e('🐎', 'vágtató ló'),
      e('🦄', 'egyszarvú'),
      e('🦓', 'zebra'),
      e('🦌', 'szarvas'),
      e('🐮', 'tehén'),
      e('🐄', 'tehén'),
      e('🐂', 'ökör'),
      e('🐃', 'bivaly'),
      e('🐷', 'malac|disznó'),
      e('🐖', 'disznó'),
      e('🐗', 'vaddisznó'),
      e('🐏', 'kos'),
      e('🐑', 'birka|juh'),
      e('🐐', 'kecske'),
      e('🐪', 'teve'),
      e('🦙', 'láma'),
      e('🦒', 'zsiráf'),
      e('🐘', 'elefánt'),
      e('🦏', 'rinocérosz|orrszarvú'),
      e('🦛', 'víziló'),
      e('🐭', 'egér'),
      e('🐁', 'egér'),
      e('🐀', 'patkány'),
      e('🐹', 'hörcsög'),
      e('🐰', 'nyuszi|nyúl'),
      e('🐇', 'nyúl'),
      e('🐿️', 'mókus'),
      e('🦔', 'sün'),
      e('🦇', 'denevér'),
      e('🐻', 'medve'),
      e('🐨', 'koala'),
      e('🐼', 'panda'),
      e('🦥', 'lajhár'),
      e('🦦', 'vidra'),
      e('🦨', 'bűzös borz'),
      e('🦘', 'kenguru'),
      e('🐔', 'csirke|tyúk'),
      e('🐓', 'kakas'),
      e('🐣', 'kikelő csibe'),
      e('🐤', 'csibe'),
      e('🐦', 'madár'),
      e('🐧', 'pingvin'),
      e('🕊️', 'galamb|béke'),
      e('🦅', 'sas'),
      e('🦆', 'kacsa'),
      e('🦢', 'hattyú'),
      e('🦉', 'bagoly'),
      e('🦜', 'papagáj'),
      e('🐸', 'béka'),
      e('🐊', 'krokodil'),
      e('🐢', 'teknős|lassú'),
      e('🦎', 'gyík'),
      e('🐍', 'kígyó'),
      e('🐲', 'sárkány'),
      e('🐳', 'bálna'),
      e('🐬', 'delfin'),
      e('🐟', 'hal'),
      e('🐠', 'trópusi hal'),
      e('🦈', 'cápa'),
      e('🐙', 'polip'),
      e('🐚', 'kagyló'),
      e('🦀', 'rák'),
      e('🦐', 'garnéla'),
      e('🐌', 'csiga|lassú'),
      e('🦋', 'pillangó|lepke'),
      e('🐛', 'hernyó|bug|hiba'),
      e('🐜', 'hangya'),
      e('🐝', 'méh'),
      e('🐞', 'katicabogár'),
      e('🕷️', 'pók'),
      e('🦠', 'mikroba|baktérium|vírus'),
      e('🐾', 'mancsnyom|tappancs'),
    ],
  },
  {
    title: 'Természet és időjárás',
    items: [
      e('💐', 'csokor'),
      e('🌸', 'cseresznyevirág'),
      e('🌹', 'rózsa'),
      e('🌺', 'hibiszkusz'),
      e('🌻', 'napraforgó'),
      e('🌼', 'margaréta'),
      e('🌷', 'tulipán'),
      e('🌱', 'palánta|növekedés'),
      e('🪴', 'cserepes növény'),
      e('🌲', 'fenyő'),
      e('🌳', 'fa'),
      e('🌴', 'pálma'),
      e('🌵', 'kaktusz'),
      e('🌾', 'kalász|gabona'),
      e('🍀', 'négylevelű lóhere|szerencse'),
      e('🍁', 'juharlevél|ősz'),
      e('🍂', 'lehullott levelek|ősz'),
      e('🍃', 'szélfútta levél'),
      e('🌍', 'föld|európa'),
      e('🌙', 'hold|éjszaka'),
      e('🌚', 'újhold arc'),
      e('🌝', 'telihold arc'),
      e('⭐', 'csillag'),
      e('🌟', 'ragyogó csillag'),
      e('✨', 'csillogás|varázslat|új'),
      e('⚡', 'villám|gyors|áram'),
      e('🔆', 'fényes'),
      e('☀️', 'nap|napos'),
      e('🌤️', 'napos felhővel'),
      e('⛅', 'felhős'),
      e('☁️', 'felhő'),
      e('🌧️', 'eső'),
      e('⛈️', 'vihar'),
      e('🌩️', 'villámlás'),
      e('🌨️', 'havazás'),
      e('🌪️', 'tornádó'),
      e('🌫️', 'köd'),
      e('🌬️', 'szél'),
      e('🌈', 'szivárvány'),
      e('❄️', 'hópehely|hideg|tél'),
      e('⛄', 'hóember'),
      e('💧', 'csepp|víz'),
      e('🌊', 'hullám|tenger'),
      e('🌋', 'vulkán'),
      e('🏔️', 'havas hegy'),
    ],
  },
  {
    title: 'Étel és ital',
    items: [
      e('🍏', 'zöld alma'),
      e('🍎', 'alma'),
      e('🍐', 'körte'),
      e('🍊', 'narancs'),
      e('🍋', 'citrom'),
      e('🍌', 'banán'),
      e('🍉', 'görögdinnye'),
      e('🍇', 'szőlő'),
      e('🍓', 'eper'),
      e('🫐', 'áfonya'),
      e('🍒', 'cseresznye'),
      e('🍑', 'őszibarack'),
      e('🥭', 'mangó'),
      e('🍍', 'ananász'),
      e('🥥', 'kókusz'),
      e('🥝', 'kivi'),
      e('🍅', 'paradicsom'),
      e('🍆', 'padlizsán'),
      e('🥑', 'avokádó'),
      e('🥦', 'brokkoli'),
      e('🥬', 'saláta'),
      e('🥒', 'uborka'),
      e('🌶️', 'csípős paprika'),
      e('🫑', 'paprika'),
      e('🌽', 'kukorica'),
      e('🥕', 'sárgarépa'),
      e('🧄', 'fokhagyma'),
      e('🧅', 'hagyma'),
      e('🥔', 'burgonya|krumpli'),
      e('🍠', 'édesburgonya'),
      e('🥐', 'croissant'),
      e('🥖', 'bagett'),
      e('🍞', 'kenyér'),
      e('🥨', 'perec'),
      e('🧀', 'sajt'),
      e('🥚', 'tojás'),
      e('🍳', 'tükörtojás|reggeli'),
      e('🥞', 'palacsinta'),
      e('🥓', 'szalonna'),
      e('🍗', 'csirkecomb'),
      e('🍖', 'hús'),
      e('🌭', 'hotdog'),
      e('🍔', 'hamburger'),
      e('🍟', 'sültkrumpli'),
      e('🍕', 'pizza'),
      e('🥪', 'szendvics'),
      e('🌮', 'taco'),
      e('🌯', 'burrito'),
      e('🥗', 'saláta tál'),
      e('🥘', 'serpenyős étel'),
      e('🍲', 'leves|egytálétel'),
      e('🍜', 'tésztaleves'),
      e('🍝', 'spagetti'),
      e('🍚', 'rizs'),
      e('🍣', 'sushi'),
      e('🍤', 'rántott garnéla'),
      e('🥟', 'gombóc'),
      e('🍦', 'fagyi'),
      e('🍰', 'sütemény'),
      e('🎂', 'torta|születésnap'),
      e('🧁', 'muffin'),
      e('🍪', 'keksz'),
      e('🍫', 'csoki'),
      e('🍬', 'cukorka'),
      e('🍯', 'méz'),
      e('🥛', 'tej'),
      e('☕', 'kávé|kávészünet'),
      e('🫖', 'teáskanna'),
      e('🍵', 'tea'),
      e('🧃', 'gyümölcslé'),
      e('🥤', 'üdítő'),
      e('🍺', 'sör'),
      e('🍻', 'koccintás|sörözés'),
      e('🥂', 'pezsgő|ünneplés'),
      e('🍷', 'bor'),
      e('🥃', 'whisky'),
      e('🍾', 'pezsgősüveg|siker'),
      e('🧊', 'jégkocka'),
      e('🍽️', 'evőeszköz|étkezés|ebéd'),
    ],
  },
  {
    title: 'Utazás és helyek',
    items: [
      e('🚗', 'autó|kocsi'),
      e('🚕', 'taxi'),
      e('🚙', 'terepjáró'),
      e('🚌', 'busz'),
      e('🚎', 'trolibusz'),
      e('🏎️', 'versenyautó|gyors'),
      e('🚓', 'rendőrautó'),
      e('🚑', 'mentő'),
      e('🚒', 'tűzoltóautó'),
      e('🚐', 'kisbusz'),
      e('🛻', 'pickup'),
      e('🚚', 'teherautó|szállítás'),
      e('🚜', 'traktor'),
      e('🦽', 'kerekesszék'),
      e('🛵', 'robogó'),
      e('🏍️', 'motor'),
      e('🚲', 'bicikli'),
      e('🛴', 'roller'),
      e('🚂', 'mozdony'),
      e('🚆', 'vonat'),
      e('🚇', 'metró'),
      e('🚊', 'villamos'),
      e('✈️', 'repülő|utazás'),
      e('🛫', 'felszállás|indulás'),
      e('🛬', 'leszállás|érkezés'),
      e('🚁', 'helikopter'),
      e('⛵', 'vitorlás'),
      e('🚢', 'hajó'),
      e('⛽', 'benzinkút'),
      e('🚦', 'jelzőlámpa'),
      e('🚧', 'útlezárás|munkálat'),
      e('🗺️', 'térkép'),
      e('🧭', 'iránytű'),
      e('🏠', 'ház|otthon'),
      e('🏡', 'kertes ház'),
      e('🏢', 'irodaház|iroda'),
      e('🏥', 'kórház|rendelő'),
      e('🏦', 'bank'),
      e('🏪', 'bolt'),
      e('🏫', 'iskola'),
      e('🏭', 'gyár'),
      e('🏨', 'szálloda'),
      e('⛪', 'templom'),
      e('🏛️', 'hivatal|klasszikus épület'),
      e('🏗️', 'építkezés'),
      e('🌇', 'naplemente a városban'),
      e('🌃', 'éjszakai város'),
      e('🌉', 'híd éjszaka'),
      e('🗼', 'torony'),
      e('🎡', 'óriáskerék'),
      e('🏖️', 'strand|nyaralás'),
      e('🏕️', 'kemping'),
      e('⛺', 'sátor'),
      e('🧳', 'bőrönd|utazás'),
    ],
  },
  {
    title: 'Tevékenység és sport',
    items: [
      e('⚽', 'foci|labdarúgás'),
      e('🏀', 'kosárlabda'),
      e('🏈', 'amerikai foci'),
      e('⚾', 'baseball'),
      e('🎾', 'tenisz'),
      e('🏐', 'röplabda'),
      e('🏉', 'rögbi'),
      e('🥏', 'frizbi'),
      e('🎱', 'biliárd'),
      e('🏓', 'pingpong'),
      e('🏸', 'tollaslabda'),
      e('🥅', 'kapu'),
      e('⛳', 'golf'),
      e('🏹', 'íjászat|cél'),
      e('🎣', 'horgászat'),
      e('🥊', 'boksz'),
      e('🥋', 'küzdősport'),
      e('⛸️', 'korcsolya'),
      e('🎿', 'sí'),
      e('🏂', 'snowboard'),
      e('🏋️', 'súlyemelés|edzés'),
      e('🤸', 'tornász'),
      e('🤺', 'vívás'),
      e('🏊', 'úszás'),
      e('🚴', 'kerékpározás'),
      e('🧗', 'sziklamászás'),
      e('🏆', 'kupa|győzelem|első'),
      e('🥇', 'aranyérem|első'),
      e('🥈', 'ezüstérem|második'),
      e('🥉', 'bronzérem|harmadik'),
      e('🏅', 'érem'),
      e('🎯', 'célpont|telitalálat'),
      e('🎲', 'dobókocka|szerencse'),
      e('🎮', 'játékvezérlő|videojáték'),
      e('🎰', 'nyerőgép'),
      e('🧩', 'kirakó|puzzle'),
      e('🎨', 'festés|kreatív'),
      e('🎭', 'színház'),
      e('🎬', 'film|klapni'),
      e('🎤', 'mikrofon|éneklés'),
      e('🎧', 'fejhallgató|zene'),
      e('🎵', 'hangjegy|zene'),
      e('🎶', 'hangjegyek|dallam'),
      e('🎸', 'gitár'),
      e('🎹', 'zongora'),
      e('🥁', 'dob'),
      e('🎺', 'trombita'),
      e('🎻', 'hegedű'),
    ],
  },
  {
    title: 'Munka és tárgyak',
    items: [
      e('💻', 'laptop|számítógép|munka'),
      e('🖥️', 'monitor|asztali gép'),
      e('⌨️', 'billentyűzet|gépelés'),
      e('🖱️', 'egér'),
      e('🖨️', 'nyomtató'),
      e('📱', 'telefon|mobil'),
      e('☎️', 'telefon'),
      e('📞', 'hívás|telefonál'),
      e('📟', 'személyhívó'),
      e('📠', 'fax'),
      e('🔋', 'akkumulátor|töltöttség'),
      e('🔌', 'konnektor|áram'),
      e('💾', 'floppy|mentés'),
      e('💿', 'lemez'),
      e('📀', 'dvd'),
      e('🧮', 'abakusz|számolás'),
      e('🎥', 'kamera|felvétel'),
      e('📷', 'fényképezőgép|fotó'),
      e('📹', 'videokamera'),
      e('📺', 'tévé'),
      e('📻', 'rádió'),
      e('🔍', 'nagyító|keresés'),
      e('🔎', 'nagyító jobbra|vizsgálat'),
      e('💰', 'pénzeszsák|bevétel'),
      e('💵', 'bankjegy|pénz'),
      e('💳', 'bankkártya|fizetés'),
      e('🧾', 'számla|nyugta'),
      e('💎', 'gyémánt|értékes'),
      e('⚖️', 'mérleg|jog|egyensúly'),
      e('🔧', 'csavarkulcs|javítás'),
      e('🔨', 'kalapács'),
      e('🛠️', 'szerszám|karbantartás'),
      e('⚙️', 'fogaskerék|beállítás|működés'),
      e('🧰', 'szerszámosláda'),
      e('🧲', 'mágnes'),
      e('🪛', 'csavarhúzó'),
      e('🔩', 'csavar és anya'),
      e('🧪', 'kémcső|kísérlet|teszt'),
      e('🧬', 'dns|genetika'),
      e('🔬', 'mikroszkóp|labor'),
      e('🔭', 'távcső'),
      e('📡', 'antenna|kapcsolat'),
      e('💊', 'gyógyszer|tabletta'),
      e('💉', 'injekció|oltás'),
      e('🩺', 'sztetoszkóp|vizsgálat'),
      e('🩹', 'sebtapasz'),
      e('🌡️', 'hőmérő|láz'),
      e('🚪', 'ajtó'),
      e('🪑', 'szék'),
      e('🛏️', 'ágy'),
      e('🚿', 'zuhany'),
      e('🧹', 'seprű|takarítás'),
      e('🧼', 'szappan'),
      e('🧴', 'flakon'),
      e('🔑', 'kulcs|hozzáférés'),
      e('🔒', 'zárva|privát'),
      e('🔓', 'nyitva|nyilvános'),
      e('🔔', 'csengő|értesítés'),
      e('🔕', 'némítva|csend'),
      e('📣', 'megafon|hirdetés'),
      e('📢', 'hangosbeszélő|bejelentés'),
      e('📝', 'jegyzet|írás'),
      e('📄', 'dokumentum|lap'),
      e('📃', 'irat'),
      e('📑', 'könyvjelzők'),
      e('📊', 'oszlopdiagram|statisztika|riport'),
      e('📈', 'növekvő grafikon|emelkedés'),
      e('📉', 'csökkenő grafikon|esés'),
      e('📋', 'vágólap|lista'),
      e('📅', 'naptár|dátum'),
      e('📆', 'naptár|időpont'),
      e('🗓️', 'naptár|ütemezés'),
      e('📇', 'kartoték'),
      e('🗂️', 'mappák|rendszerezés'),
      e('📁', 'mappa'),
      e('📂', 'nyitott mappa'),
      e('🗃️', 'irattartó'),
      e('🗄️', 'iratszekrény|archívum'),
      e('🗑️', 'kuka|törlés'),
      e('📦', 'doboz|csomag|szállítmány'),
      e('📮', 'postaláda'),
      e('✉️', 'boríték|levél|e-mail'),
      e('📧', 'e-mail'),
      e('📨', 'beérkező üzenet'),
      e('📤', 'kimenő'),
      e('📥', 'bejövő'),
      e('🔖', 'könyvjelző|címke'),
      e('🏷️', 'címke|megjelölés'),
      e('📎', 'gemkapocs|csatolmány'),
      e('🖇️', 'kapcsok'),
      e('📏', 'vonalzó'),
      e('📐', 'háromszögvonalzó'),
      e('✂️', 'olló|kivágás'),
      e('🖊️', 'toll'),
      e('🖋️', 'töltőtoll'),
      e('✏️', 'ceruza|szerkesztés'),
      e('🖌️', 'ecset'),
      e('🖍️', 'zsírkréta'),
      e('📚', 'könyvek|tanulás|tudás'),
      e('📖', 'nyitott könyv|olvasás'),
      e('📓', 'füzet'),
      e('📔', 'jegyzetfüzet'),
      e('🗒️', 'jegyzettömb'),
      e('📰', 'újság|hír'),
      e('🗞️', 'összetekert újság'),
      e('⏰', 'ébresztőóra|riasztás'),
      e('⏱️', 'stopper|időmérés'),
      e('⏲️', 'időzítő'),
      e('🕐', 'óra|idő'),
      e('⌛', 'lejárt homokóra|határidő'),
      e('🔗', 'lánc|link'),
      e('🕯️', 'gyertya'),
      e('🔦', 'zseblámpa'),
      e('🪫', 'lemerült akkumulátor'),
      e('🎁', 'ajándék'),
      e('🎈', 'lufi|ünnep'),
      e('🎀', 'masni'),
      e('🧸', 'plüssmaci'),
      e('🪄', 'varázspálca'),
      e('🛎️', 'recepciós csengő'),
    ],
  },
  {
    title: 'Jelek és szimbólumok',
    items: [
      e('❗', 'felkiáltójel|fontos'),
      e('❓', 'kérdőjel|kérdés'),
      e('‼️', 'két felkiáltójel|nagyon fontos'),
      e('⁉️', 'kérdő felkiáltás'),
      e('💯', 'száz pont|tökéletes|egyetértek'),
      e('🔴', 'piros kör|kritikus'),
      e('🟠', 'narancs kör'),
      e('🟡', 'sárga kör|figyelem'),
      e('🟢', 'zöld kör|rendben|kész'),
      e('🔵', 'kék kör'),
      e('🟣', 'lila kör'),
      e('⚫', 'fekete kör'),
      e('⚪', 'fehér kör'),
      e('🟥', 'piros négyzet'),
      e('🟧', 'narancs négyzet'),
      e('🟨', 'sárga négyzet'),
      e('🟩', 'zöld négyzet'),
      e('🟦', 'kék négyzet'),
      e('⬛', 'fekete négyzet'),
      e('⬜', 'fehér négyzet'),
      e('✔️', 'pipa|kész'),
      e('☑️', 'bepipált négyzet|elvégezve'),
      e('✖️', 'szorzás|nem'),
      e('➕', 'plusz|hozzáad'),
      e('➖', 'mínusz|elvesz'),
      e('➗', 'osztás'),
      e('🟰', 'egyenlő'),
      e('♾️', 'végtelen'),
      e('💲', 'dollár'),
      e('💱', 'valutaváltás'),
      e('™️', 'védjegy'),
      e('©️', 'copyright'),
      e('®️', 'bejegyzett védjegy'),
      e('🔝', 'top|legfelül'),
      e('🔜', 'hamarosan'),
      e('🆕', 'új'),
      e('🆓', 'ingyenes'),
      e('🆙', 'frissítve'),
      e('🈵', 'megtelt'),
      e('🚫', 'tilos|nem szabad'),
      e('⛔', 'behajtani tilos|állj'),
      e('🔞', 'tizennyolc éven felülieknek'),
      e('☢️', 'radioaktív'),
      e('☣️', 'biológiai veszély'),
      e('⬆️', 'felfelé nyíl'),
      e('⬇️', 'lefelé nyíl'),
      e('⬅️', 'balra nyíl|vissza'),
      e('➡️', 'jobbra nyíl|tovább'),
      e('↩️', 'visszatérés|válasz'),
      e('↪️', 'továbbítás'),
      e('🔄', 'körkörös nyilak|frissítés|ismétlés'),
      e('🔃', 'függőleges körforgás'),
      e('▶️', 'lejátszás|indítás'),
      e('⏸️', 'szünet'),
      e('⏹️', 'megállítás'),
      e('⏺️', 'felvétel'),
      e('⏭️', 'következő'),
      e('⏮️', 'előző'),
      e('⏩', 'gyors előre'),
      e('🔀', 'véletlen sorrend'),
      e('🔁', 'ismétlés'),
      e('🔊', 'hangos'),
      e('🔉', 'közepes hangerő'),
      e('🔈', 'halk'),
      e('🔇', 'néma'),
      e('🎙️', 'stúdiómikrofon'),
      e('📶', 'térerő|jel'),
      e('📳', 'rezgés'),
      e('♻️', 'újrahasznosítás'),
      e('⚜️', 'liliom'),
      e('🔱', 'háromágú szigony'),
      e('⭕', 'piros kör|hiba'),
      e('❤️‍🔥', 'lángoló szív'),
      e('🧡', 'narancs szív'),
      e('💛', 'sárga szív'),
      e('💚', 'zöld szív'),
      e('💙', 'kék szív'),
      e('💜', 'lila szív'),
      e('🖤', 'fekete szív'),
      e('🤍', 'fehér szív'),
      e('🤎', 'barna szív'),
      e('💔', 'törött szív'),
      e('💕', 'két szív'),
      e('💖', 'csillogó szív'),
      e('💗', 'növekvő szív'),
      e('💘', 'nyilazott szív'),
      e('💝', 'szív masnival'),
      e('💤', 'alszik|zzz'),
      e('💬', 'buborék|üzenet|beszélgetés'),
      e('🗨️', 'bal buborék'),
      e('🗯️', 'dühös buborék'),
      e('💭', 'gondolatbuborék'),
      e('🏳️', 'fehér zászló|megadás'),
      e('🏴', 'fekete zászló'),
      e('🏁', 'kockás zászló|cél'),
      e('🚩', 'jelzőzászló|figyelmeztetés'),
      e('🇭🇺', 'magyar zászló|magyarország'),
      e('🇪🇺', 'európai unió'),
    ],
  },
];

/** Gyors reakcio-keszlet: a valaszto elso, cimke nelkuli sorai. */
export const QUICK_REACTIONS = EMOJI_GROUPS[0]!.items.map((item) => item.char);

/** Egy kattintassal elerheto, a lebego sav elso ket gombja. */
export const PRIMARY_REACTIONS = ['👍', '✅'] as const;

/** Ekezet-fuggetlen egyezes, hogy a "kave" a "kávé"-t is megtalalja. */
const fold = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/** Egy jel osszes keresoszava: a magyar kulcsszavak es az angol aliasok. */
function allKeywords(item: EmojiEntry): string[] {
  const alias = EMOJI_ALIASES[item.char];
  return alias ? [...item.keywords, ...alias.split('|')] : item.keywords;
}

/**
 * Kereses a keszletben - magyarul ES angolul.
 *
 * Ures keresesre a csoportokat valtozatlanul adjuk vissza - igy a valaszto
 * bongeszheto is marad, nem csak kereshető.
 *
 * ─── Miert szamit a SORREND? ────────────────────────────────────────────────
 *
 * A szerkesztoben legfeljebb nyolc talalat fer ki, tehat nem mindegy, mi all
 * a lista elejen. Harom kosarba valogatunk:
 *
 *   1. PONTOS egyezes - a beirt szo egy az egyben egy keresoszo,
 *   2. ELOTAG - egy keresoszo a beirt szoval kezdodik,
 *   3. minden mas illeszkedes.
 *
 * A pontos kosar nelkul a ":smile" a 😃-t (`smiley`) hozna elore a 😄
 * (`smile`) helyett, mert az elobbi hamarabb all a keszletben. A kosarakon
 * belul a SAJAT hasznalat dont (`rank`: jel -> pontszam, lasd
 * data/emoji-usage.ts): a ":sz" annak a 😍-t hozza elore, aki azt szokta, nem
 * a keszletben elobb allo jelet. Hasznalat nelkul marad a keszlet sorrendje.
 */
export function searchEmoji(query: string, rank?: ReadonlyMap<string, number>): EmojiGroup[] {
  const needle = fold(query.trim());
  if (!needle) return EMOJI_GROUPS;

  const pontos: EmojiEntry[] = [];
  const elotag: EmojiEntry[] = [];
  const tobbi: EmojiEntry[] = [];
  const seen = new Set<string>();

  for (const group of EMOJI_GROUPS) {
    for (const item of group.items) {
      if (seen.has(item.char)) continue;

      const szavak = allKeywords(item).map(fold);
      if (!szavak.some((keyword) => keyword.includes(needle))) continue;

      seen.add(item.char);
      if (szavak.some((keyword) => keyword === needle)) pontos.push(item);
      else if (szavak.some((keyword) => keyword.startsWith(needle))) elotag.push(item);
      else tobbi.push(item);
    }
  }

  if (rank && rank.size > 0) {
    // A rendezes stabil: az egyforma pontszamuak megtartjak a keszlet-sorrendet.
    const byUse = (a: EmojiEntry, b: EmojiEntry) =>
      (rank.get(b.char) ?? 0) - (rank.get(a.char) ?? 0);
    pontos.sort(byUse);
    elotag.sort(byUse);
    tobbi.sort(byUse);
  }

  const hits = [...pontos, ...elotag, ...tobbi];
  return hits.length > 0 ? [{ title: 'Találatok', items: hits }] : [];
}

let byCharCache: Map<string, EmojiEntry> | undefined;

/** A keszlet bejegyzese egy jelhez - a nevehez es a keresoszavaihoz. */
export function findEmoji(char: string): EmojiEntry | undefined {
  if (!byCharCache) {
    byCharCache = new Map();
    for (const group of EMOJI_GROUPS) {
      for (const item of group.items)
        if (!byCharCache.has(item.char)) byCharCache.set(item.char, item);
    }
  }
  return byCharCache.get(char);
}

/**
 * Egy jel a PONTOS angol rovidnevehez - a `:salute:` alak atalakitasahoz.
 *
 * Csak teljes egyezes szamit: a `:sal:` nem ad vissza semmit. Reszleges
 * egyezesre ott a javaslo-lista; ez az ut arrol szol, hogy aki mar tudja a
 * nevet, vegigirhassa.
 */
export function emojiByShortcode(name: string): string | undefined {
  const kulcs = fold(name);
  // A `heart_eyes`, a `heart-eyes` es a `hearteyes` ugyanaz a nev.
  return shortcodeIndex().get(kulcs) ?? shortcodeIndex().get(kulcs.replace(/[\s_-]+/g, ''));
}

let indexCache: Map<string, string> | undefined;

function shortcodeIndex(): Map<string, string> {
  if (indexCache) return indexCache;

  const map = new Map<string, string>();
  for (const group of EMOJI_GROUPS) {
    for (const item of group.items) {
      for (const keyword of allKeywords(item)) {
        /*
          A tobbszavas nevek szokoz nelkul IS mukodjenek.

          A rovidnev-alak nem tur szokozt (`:heart eyes:` sosem allna ossze),
          a bevett irasmod pedig hol alahuzas, hol semmi: `:heart_eyes:` es
          `:heartEyes:` egyarant elofordul. A foldolt kulcsban mindket jel
          eltunik, igy mindharom alak ugyanoda mutat.
        */
        const kulcs = fold(keyword);
        if (!map.has(kulcs)) map.set(kulcs, item.char);

        const szokoztalan = kulcs.replace(/[\s_-]+/g, '');
        if (szokoztalan !== kulcs && !map.has(szokoztalan)) map.set(szokoztalan, item.char);
      }
    }
  }

  indexCache = map;
  return map;
}
